import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useState } from "react";
import { useTemplateWithFields } from "./useTemplateWithFields";
type RuleSet = Database["public"]["Tables"]["template_field_rule_sets"]["Row"] & {
  rules: Database["public"]["Tables"]["template_field_rules"]["Row"][];
};

interface UseTemplateFieldRuleSetsOptions {
  templateFieldId?: string;
}

export const useTemplateFieldRuleSets = ({ templateFieldId }: UseTemplateFieldRuleSetsOptions) => {
  const { template, isPending, setQueryData } = useTemplateWithFields();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  if (!template) {
    throw new Error("Template not found");
  }

  const templateField = template.template_fields.find((field) => field.id === templateFieldId);

  if (!templateField) {
    throw new Error("Template field not found");
  }

  const ruleSets = templateField.rule_sets;

  const createRuleSet = async ({ name, description }: { name: string; description?: string }) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("template_field_rule_sets")
        .insert({
          name,
          description: description ?? null,
          template_field_id: templateFieldId!,
          order_index: ruleSets.length, // Add to end of list
        })
        .select()
        .single();

      if (error) throw error;

      // Update the cache
      setQueryData((oldData) => ({
        ...oldData,
        template_fields: oldData.template_fields.map((field) => {
          if (field.id === templateFieldId) {
            return {
              ...field,
              rule_sets: [
                ...field.rule_sets,
                {
                  ...data,
                  rules: [], // Initialize with empty rules array
                },
              ],
            };
          }
          return field;
        }),
      }));

      return data;
    } catch (err) {
      console.error("Error creating rule set:", err);
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateRuleSet = async ({ id, name, description }: { id: string; name: string; description?: string }) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("template_field_rule_sets")
        .update({
          name,
          description,
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      // Update the cache
      setQueryData((oldData) => ({
        ...oldData,
        template_fields: oldData.template_fields.map((field) => {
          if (field.id === templateFieldId) {
            return {
              ...field,
              rule_sets: field.rule_sets.map((ruleSet) => {
                if (ruleSet.id === id) {
                  return {
                    ...ruleSet,
                    name,
                    description: description ?? null,
                  };
                }
                return ruleSet;
              }),
            };
          }
          return field;
        }),
      }));

      return data;
    } catch (err) {
      console.error("Error updating rule set:", err);
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteRuleSet = async (id: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.from("template_field_rule_sets").delete().eq("id", id);

      if (error) throw error;

      // Update the cache
      setQueryData((oldData) => ({
        ...oldData,
        template_fields: oldData.template_fields.map((field) => {
          if (field.id === templateFieldId) {
            return {
              ...field,
              rule_sets: field.rule_sets.filter((ruleSet) => ruleSet.id !== id),
            };
          }
          return field;
        }),
      }));
    } catch (err) {
      console.error("Error deleting rule set:", err);
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const createRule = async (
    ruleSetId: string,
    rule: {
      fieldId: string;
      datapointId: string;
      operator: "AND" | "NOT";
    },
  ) => {
    try {
      setIsLoading(true);

      const { data, error } = await supabase
        .from("template_field_rules")
        .insert({
          rule_set_id: ruleSetId,
          field_id: rule.fieldId,
          datapoint_id: rule.datapointId,
          operator: rule.operator,
          order_index: ruleSets.length,
        })
        .select(
          `
          *,
          datapoint:datapoints (
            id,
            name
          )
        `,
        )
        .single();

      if (error) throw error;

      // Update the cache
      setQueryData((oldData) => ({
        ...oldData,
        template_fields: oldData.template_fields.map((field) => {
          if (field.id === templateFieldId) {
            return {
              ...field,
              rule_sets: field.rule_sets.map((ruleSet) => {
                if (ruleSet.id === ruleSetId) {
                  return {
                    ...ruleSet,
                    rules: [...ruleSet.rules, data],
                  };
                }
                return ruleSet;
              }),
            };
          }
          return field;
        }),
      }));

      return data;
    } catch (err) {
      console.error("Error creating rule:", err);
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateRule = async (
    ruleId: string,
    {
      fieldId,
      datapointId,
      operator,
    }: {
      fieldId: string;
      datapointId: string;
      operator: "AND" | "NOT";
    },
  ) => {
    try {
      setIsLoading(true);

      const { data, error } = await supabase
        .from("template_field_rules")
        .update({
          field_id: fieldId,
          datapoint_id: datapointId,
          operator,
        })
        .eq("id", ruleId)
        .select(
          `
          *,
          datapoint:datapoints (
            id,
            name
          )
        `,
        )
        .single();

      if (error) throw error;

      // Update the cache
      setQueryData((oldData) => ({
        ...oldData,
        template_fields: oldData.template_fields.map((field) => {
          if (field.id === templateFieldId) {
            return {
              ...field,
              rule_sets: field.rule_sets.map((ruleSet) => {
                return {
                  ...ruleSet,
                  rules: ruleSet.rules.map((rule) => {
                    if (rule.id === ruleId) {
                      return {
                        ...rule,
                        field_id: fieldId,
                        datapoint_id: datapointId,
                        operator,
                      };
                    }
                    return rule;
                  }),
                };
              }),
            };
          }
          return field;
        }),
      }));

      return data;
    } catch (err) {
      console.error("Error updating rule:", err);
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteRule = async (ruleId: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.from("template_field_rules").delete().eq("id", ruleId);

      if (error) throw error;

      // Update the cache
      setQueryData((oldData) => ({
        ...oldData,
        template_fields: oldData.template_fields.map((field) => {
          if (field.id === templateFieldId) {
            return {
              ...field,
              rule_sets: field.rule_sets.map((ruleSet) => {
                return {
                  ...ruleSet,
                  rules: ruleSet.rules.filter((rule) => rule.id !== ruleId),
                };
              }),
            };
          }
          return field;
        }),
      }));
    } catch (err) {
      console.error("Error deleting rule:", err);
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    ruleSets,
    createRuleSet,
    updateRuleSet,
    deleteRuleSet,
    createRule,
    updateRule,
    deleteRule,
    isLoading,
    isPending,
    error,
  };
};
